/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useDrag } from 'react-dnd';
import { Flyout } from '@els/els-react--flyout';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { editHistoryQuery as editHistoryQueryAction } from '@Pages/QueryBuilder/QueryletActions';
import QuickSearchQuery from '@Pages/QuickSearch/Components/QuickSearchQuery/QuickSearchQuery';
import { recordCountOrTotalResultFromHistoryItem } from '@Containers/PatentFamilyClustering/selectors';
import { commaFormatNumber, replaceCitations } from '@Utils/String';

import './HistoryDragNode.scss';

function HistoryDragNode({ node, addToBuilder }) {
  const [isAdded, setIsAdded] = useState(false);
  const addedTimerRef = useRef(null);

  const triggerAddedState = () => {
    setIsAdded(true);
    if (addedTimerRef.current) {
      clearTimeout(addedTimerRef.current);
    }
    addedTimerRef.current = setTimeout(() => {
      setIsAdded(false);
      addedTimerRef.current = null;
    }, 1000);
  };

  const [{ isDragging }, dragRef] = useDrag(
    {
      type: 'DragNode',
      item: { node, type: 'DragNode' },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (_item, monitor) => {
        if (monitor.didDrop()) {
          triggerAddedState();
        }
      },
    },
    [node]
  );
  const dispatch = useDispatch();

  const editHistoryQuery = () => {
    dispatch(editHistoryQueryAction(node));
  };

  useEffect(() => {
    if (isDragging) document.body.style.cursor = 'move !important';
    else document.body.style.cursor = 'normal';
  }, [isDragging]);

  useEffect(
    () => () => {
      if (addedTimerRef.current) {
        clearTimeout(addedTimerRef.current);
      }
    },
    []
  );

  const handleAddToBuilder = (n) => {
    addToBuilder(n);
    triggerAddedState();
  };

  return (
    <div className="query-builder-search-section__drag-node">
      <div
        className="query-builder-search-section__drag-node-content"
        onClick={(e) => e.currentTarget === e.target && handleAddToBuilder(node)}
        onKeyDown={(e) => e.currentTarget === e.target && handleAddToBuilder(node)}>
        <Button
          type="link"
          className="u-els-ellipsis"
          data-e2e={`history-item-${node.id}`}
          onClick={(e) => {
            handleAddToBuilder(node);
            e.stopPropagation();
          }}>
          <div className="qb-hsitroy-node-header u-els-margin-bottom-1o4">
            {`${commaFormatNumber(recordCountOrTotalResultFromHistoryItem(node))} ${replaceCitations(node.context)}`}
          </div>
          <QuickSearchQuery querylet={node} />
        </Button>
        <div style={{ display: 'flex' }}>
          {(node.whereClauseImportToken || node?.quickSearchItem?.whereClauseImportToken || node.queryFactItem?.id) && (
            <Flyout
              trigger="hover"
              className="qb-drag-node-icon"
              flyout={<span aria-hidden="true">Edit query</span>}
              placement="bottom"
              theme="simple">
              <ButtonWithIcon
                id={`edit-history-query-${node.id}`}
                iconOnly
                type="link"
                data-e2e={`edit-history-query-${node.id}`}
                linkIconSize="s"
                size="small"
                sprite="Edit"
                aria-label="Edit query"
                onClick={editHistoryQuery}>
                Edit history item
              </ButtonWithIcon>
            </Flyout>
          )}
          <div aria-live="polite" aria-atomic="true">
            {isDragging && <span className="sr-only">{node.displayName || 'Query'} is being dragged to query builder</span>}
            {isAdded && <span className="sr-only">{node.displayName || 'Query'} added to query builder</span>}
          </div>
          <div ref={dragRef}>
            <Flyout trigger="hover" flyout={<span aria-hidden="true">Drag query</span>} placement="bottom" theme="simple">
              <ButtonWithIcon
                id={`querybuilder-draghistory-${node.id}`}
                draggable
                className="cursor-move"
                onClick={(dragE) => dragE.stopPropagation()}
                linkIconSize="xxs"
                iconOnly
                data-e2e={`querybuilder-draghistory-${node.id}`}
                type="borderless"
                aria-grabbed={isDragging}
                sprite="Drag"
                tabIndex="-1"
                aria-label="Drag query">
                drag
              </ButtonWithIcon>
            </Flyout>
          </div>
        </div>
      </div>
    </div>
  );
}

HistoryDragNode.propTypes = {
  node: PropTypes.object,
  addToBuilder: PropTypes.func,
};

export default HistoryDragNode;
